import React, { useState, useEffect, useRef } from "react";
import { array, bool, func, number } from "prop-types";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";
import { twMerge } from "tailwind-merge";

import { Trans, useTranslation } from "@lib/i18n";

import api from "@api/index";

import CarteGriseDetail from "./CarteGriseDetail";
import CitroenRegistration from "./CitroenRegistration";
import CitroenRegistrationPrice from "./CitroenRegistrationPrice";
import ConfirmCEESubsidyModal from "./ConfirmCEESubsidyModal";
import ConfirmChoiceModal from "./ConfirmChoiceModal";
import Radio from "../shared/Radio";
import Typography from "../shared/Typography";
import InfoIcon from "@components/shared/Icons/InfoIcon";
import LoadingCircle from "@components/shared/Icons/LoadingCircle";
import WarningIcon from "@components/shared/Icons/Warning";
import SelectField from "@components/shared/SelectField";
import StickyRequestButton from "@components/shared/StickyRequestButton";

import { useRoutingUtils } from "@hooks/useRoutingUtils";
import { useGetJourneySpecificConfig } from "@hooks/useGetJourneySpecificConfig";
import { useFetch } from "@hooks/useFetch";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import {
  useCarteGriseConfigSelector,
  useCEESubsidySelector,
  useConfigurationSelector,
  useCurrencySettingsSelector,
} from "@redux/reducers/appConfig";
import {
  useCurrentDealSelector,
  useIsCEESubsidySelectedSelector,
} from "@redux/reducers/deal";
import { useIsKeyAccount } from "@redux/reducers/auth";

import { ROUTES } from "@shared/routing/routes";
import { dealType } from "@types";
import { getIsVehicleRegistrationByCitroenSelected } from "@shared/v2/lib/getIsVehicleRegistrationByCitroenSelected";
import { getIsVehicleRegistrationByCustomerSelected } from "@shared/v2/lib/getIsVehicleRegistrationByCustomerSelected";
import { getIsVehicleRegistrationValid } from "@shared/v2/lib/getVehicleRegistrationType";
import { getCarteGriseAmount } from "@shared/v2/lib/getCarteGriseAmount";
import { getFinanceSimulationCarteGrise } from "@shared/v2/lib/getDealCarteGrise";
import { getFormattedPrice } from "@shared/v2/lib/getFormattedPrice";
import { getDealMyFilesConfig } from "@lib/getDealMyFilesConfig";
import { isRegistrationCostValid } from "@lib/isRegistrationCostValid";
import { getIsCashJourneySelected } from "@shared/v2/lib/getIsCashJourneySelected";
import { toast } from "@lib/reactToastify";

import {
  postDealAction,
  setVehicleRegistrationRegionAction,
} from "@redux/actions/dealActions";

import {
  BUSINESS_MODELS,
  CTA_SOL_TYPES,
  DEAL_STATES,
  DEAL_TYPE,
  VEHICLE_REGISTRATION_METHOD,
} from "@shared/constants";

const RegistrationForm = ({
  deal,
  availableMethods,
  disabled,
  fixedHeaderHeight,
  initialGTMSent,
  onVehicleRegistrationSelected,
  onVehicleRegistrationConfirmed,
  updateVehicleRegistration,
  openRegistrationCostChangedModal,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const routingUtils = useRoutingUtils();
  const currency = useCurrencySettingsSelector();
  const currentDeal = useCurrentDealSelector();
  const appConfig = useConfigurationSelector();
  const isB2BSelected = useIsB2BSelected();
  const isKeyAccount = useIsKeyAccount();
  const ceeSubsidySelected = useIsCEESubsidySelectedSelector();
  const ceeSubsidy = useCEESubsidySelector(ceeSubsidySelected);
  const journeyConfiguration = useGetJourneySpecificConfig();
  const carteGriseConfig = useCarteGriseConfigSelector();

  const { financeSimulation, userProfile } = deal;
  const { registrationPostalCode } = userProfile || {};
  const { vehicleRegistration, journey } = financeSimulation;
  const { CITROEN, MYSELF } = VEHICLE_REGISTRATION_METHOD;

  const dealMyFilesConfig = getDealMyFilesConfig(deal, appConfig);
  const isMyFilesRegistrationRegionSelectEnabled =
    !isKeyAccount &&
    !!(dealMyFilesConfig.enabled && dealMyFilesConfig.registrationRegionSelect);

  const dealCarteGrise = getFinanceSimulationCarteGrise(financeSimulation);
  const carteGrisePrice = getCarteGriseAmount(financeSimulation, true, false);
  const isCarteGriseVisible = !!carteGrisePrice;

  const isCashJourneySelected = getIsCashJourneySelected(journey);
  const isRegistrationByCitroen = getIsVehicleRegistrationByCitroenSelected(
    vehicleRegistration
  );
  const isRegistrationByCustomer = getIsVehicleRegistrationByCustomerSelected(
    vehicleRegistration
  );

  const isSelfRegistrationEnabled = availableMethods?.some(
    method => method.method === MYSELF
  );
  const isCitroenRegistrationEnabled = availableMethods?.some(
    method => method.method === CITROEN
  );

  const selfRegistrationPrice =
    journeyConfiguration?.vehicleRegistration?.selfRegistration?.price;

  const isDefaultRegistrationSelected = getIsVehicleRegistrationValid(
    currentDeal?.financeSimulation?.vehicleRegistration
  );

  const showChoiceConfirmationModal =
    isCitroenRegistrationEnabled &&
    isSelfRegistrationEnabled &&
    isCashJourneySelected &&
    isRegistrationByCustomer;

  const showCEESubsidyConfirmationModal =
    !isB2BSelected && ceeSubsidy?.enabled && !!ceeSubsidySelected;

  const [selectedRegistrationRegion, setSelectedRegistrationRegion] = useState(
    registrationPostalCode?.substr(0, 2) || null
  );

  const [
    registrationRegions,
    isRegistrationRegionsLoading,
    registrationRegionsError,
    fetchRegistrationRegions,
  ] = useFetch(api.getRegistrationRegionsList, [], false);

  const [isChoiceConfirmModalOpen, setIsChoiceConfirmModalOpen] = useState(
    false
  );
  const [
    isCEESubsidyConfirmModalOpen,
    setIsCEESubsidyConfirmModalOpen,
  ] = useState(false);
  const isSelfRegistrationConfirmed = useRef(false);

  useEffect(() => {
    if (!initialGTMSent) {
      return;
    }
    if (!availableMethods || availableMethods.length !== 1) {
      return;
    }

    const onlyMethod = availableMethods[0];
    const currentSelectionType =
      typeof vehicleRegistration === "string"
        ? vehicleRegistration
        : vehicleRegistration?.method ?? vehicleRegistration?.type;

    if (currentSelectionType === onlyMethod.method) {
      return;
    }

    updateVehicleRegistration(onlyMethod.method);
  }, [initialGTMSent, availableMethods?.length, vehicleRegistration]);

  useEffect(() => {
    if (
      isMyFilesRegistrationRegionSelectEnabled &&
      registrationRegions === undefined
    ) {
      fetchRegistrationRegions();
    }
  }, []);

  useEffect(() => {
    setSelectedRegistrationRegion(registrationPostalCode?.substr(0, 2) || null);
  }, [registrationPostalCode]);

  useEffect(() => {
    if (!isRegistrationByCitroen) {
      return;
    }
    if (!registrationRegions?.data?.length || !selectedRegistrationRegion) {
      return;
    }

    const selectedRegion = registrationRegions.data.find(
      region => region.countyCode === selectedRegistrationRegion
    );

    if (
      !selectedRegion ||
      financeSimulation.vehicleRegistrationRegion?.countyCode ===
        selectedRegion.countyCode
    ) {
      return;
    }

    dispatch(setVehicleRegistrationRegionAction(selectedRegion));
  }, [
    registrationRegions,
    selectedRegistrationRegion,
    isRegistrationByCitroen,
  ]);

  const handleRegistrationChange = event => {
    isSelfRegistrationConfirmed.current = false;
    onVehicleRegistrationSelected(event);
  };

  const triggerRequest = async () => {
    return dispatch(
      postDealAction(
        CTA_SOL_TYPES.OFFER_CREATED,
        DEAL_TYPE.DEAL,
        DEAL_STATES.OFFER
      )
    );
  };

  const handleSelfRegistrationConfirm = async () => {
    isSelfRegistrationConfirmed.current = true;
    await triggerRequest();
    setIsChoiceConfirmModalOpen(false);
  };

  const onRequestSuccess = async () => {
    await router.push(
      ROUTES.OFFER_SUMMARY,
      routingUtils.getLocalizedPathname(
        ROUTES.OFFER_SUMMARY,
        null,
        i18n.language
      )
    );

    return new Promise(() => null);
  };

  const onContinueButtonClick = async () => {
    await onVehicleRegistrationConfirmed();

    if (isRegistrationByCitroen && carteGriseConfig?.enabled) {
      try {
        const isCarteGriseValid = await isRegistrationCostValid({
          journeyType: financeSimulation.journey,
          businessModel: isB2BSelected
            ? BUSINESS_MODELS.B2B
            : BUSINESS_MODELS.B2C,
          dealCarteGrise,
          registrationPostalCode,
        });

        if (!isCarteGriseValid) {
          openRegistrationCostChangedModal();
          return false;
        }
      } catch (error) {
        toast.error(t("notify.apiDefaultError"));
        return false;
      }
    }

    if (
      !isSelfRegistrationConfirmed.current &&
      showChoiceConfirmationModal &&
      !isChoiceConfirmModalOpen
    ) {
      setIsChoiceConfirmModalOpen(true);
      return false;
    }

    if (!isSelfRegistrationConfirmed.current) {
      await triggerRequest();
    }

    if (showCEESubsidyConfirmationModal) {
      setIsCEESubsidyConfirmModalOpen(true);
      return false;
    }

    await onRequestSuccess();

    return new Promise(() => null);
  };

  const handleRegistrationRegionChange = event => {
    const selectedRegion = registrationRegions.data.find(
      region => region.countyCode === event.target.value
    );

    setSelectedRegistrationRegion(event.target.value);

    dispatch(setVehicleRegistrationRegionAction(selectedRegion));
  };

  return (
    <>
      {isSelfRegistrationEnabled && isCitroenRegistrationEnabled ? (
        <>
          <Typography className="text-sm">
            <Trans i18nKey="basket.vehicleRegistration.description" />
          </Typography>
          <div
            className={twMerge(
              "mt-4 mb-2 rounded-md px-2 py-4 border transition duration-300",
              isRegistrationByCitroen
                ? "bg-white border-greyLight"
                : "border-greyLight/50 hover:bg-white/80"
            )}
          >
            <div className="flex justify-between">
              <Radio
                checked={isRegistrationByCitroen}
                label={t("basket.vehicleRegistration.optionCitroen.button")}
                id={CITROEN}
                onChange={handleRegistrationChange}
                dataId="details-pers-new-client-radio"
              />
              <div className="flex gap-2">
                <CitroenRegistrationPrice deal={deal} />
                {isCarteGriseVisible && (
                  <Typography className="font-semibold whitespace-nowrap">
                    +
                    {getFormattedPrice(
                      carteGrisePrice,
                      currency.format,
                      currency.symbol
                    )}
                  </Typography>
                )}
              </div>
            </div>
            <CitroenRegistration
              deal={deal}
              registrationType={vehicleRegistration}
            />
          </div>
          <div
            className={twMerge(
              "mt-4 mb-2 rounded-md px-2 py-4 border transition duration-300",
              isRegistrationByCustomer
                ? "bg-white border-greyLight"
                : "border-greyLight/50 hover:bg-white/80"
            )}
          >
            <Radio
              checked={isRegistrationByCustomer}
              label={t("basket.vehicleRegistration.optionByMyself.button")}
              id={MYSELF}
              onChange={handleRegistrationChange}
              dataId="details-pers-existing-client-radio"
            />
            <motion.div
              initial="collapsed"
              animate={isRegistrationByCustomer ? "open" : "collapsed"}
              exit="collapsed"
              variants={{
                open: {
                  opacity: 1,
                  height: "auto",
                  display: "block",
                },
                collapsed: {
                  opacity: 0,
                  height: 0,
                  transitionEnd: { display: "none" },
                },
              }}
            >
              <div className="pl-10 py-2">
                <Typography
                  data-id="basket-summary-configuration-heading"
                  className="text-sm"
                >
                  {t("basket.vehicleRegistration.optionByMyself.description", {
                    price: getFormattedPrice(
                      selfRegistrationPrice,
                      currency.format,
                      currency.symbol
                    ),
                  })}
                </Typography>
              </div>
            </motion.div>
          </div>
        </>
      ) : (
        <>
          {isCitroenRegistrationEnabled && (
            <>
              <Typography className="text-sm">
                <Trans
                  i18nKey="basket.vehicleRegistration.optionCitroen.description"
                  values={{
                    price: getFormattedPrice(
                      selfRegistrationPrice,
                      currency.format,
                      currency.symbol
                    ),
                  }}
                />
              </Typography>
              <div className="flex justify-end w-full mt-4">
                <CitroenRegistrationPrice deal={deal} />
              </div>
              {!!isCarteGriseVisible && <CarteGriseDetail deal={deal} />}
            </>
          )}
          {isSelfRegistrationEnabled && (
            <Typography className="text-sm">
              <Trans
                i18nKey="basket.vehicleRegistration.optionByMyself.description"
                values={{
                  price: getFormattedPrice(
                    selfRegistrationPrice,
                    currency.format,
                    currency.symbol
                  ),
                }}
              />
            </Typography>
          )}
        </>
      )}

      {isRegistrationByCitroen && isMyFilesRegistrationRegionSelectEnabled && (
        <div className="mt-8 border-t border-t-greyLight pt-4">
          {isRegistrationRegionsLoading && (
            <div className="grid place-items-center w-full">
              <LoadingCircle size="lg" />
            </div>
          )}
          {!isRegistrationRegionsLoading && registrationRegionsError && (
            <div className="w-full">
              <Typography>
                {t("basket.vehicleRegistration.registrationRegion.labelPrompt")}
              </Typography>
              <div className="flex flex-col smallMobile:flex-row items-start smallMobile:items-center justify-between gap-4 mt-2 w-full">
                <span className="flex items-center gap-2 text-red-500">
                  <WarningIcon />
                  {t("basket.vehicleRegistration.registrationRegion.error")}
                </span>
                <button
                  className="text-red underline px-2 py-1 cursor-pointer transition-all duration-100 hover:text-primaryOrange hover:scale-105 active:scale-95"
                  onClick={fetchRegistrationRegions}
                >
                  {t("general.retry")}
                </button>
              </div>
            </div>
          )}
          {!isRegistrationRegionsLoading &&
            !registrationRegionsError &&
            registrationRegions?.data.length > 0 && (
              <>
                <SelectField
                  required
                  fullWidth
                  className="mt-0"
                  name="myFilesRegistrationRegion"
                  options={
                    registrationRegions?.data.map(({ countyCode, label }) => ({
                      value: countyCode,
                      label: [countyCode, label].join(" - "),
                    })) || []
                  }
                  value={selectedRegistrationRegion}
                  onChange={handleRegistrationRegionChange}
                  label={t(
                    "basket.vehicleRegistration.registrationRegion.labelPrompt"
                  )}
                />
                <div className="flex items-start gap-2 pl-2 pt-4">
                  <InfoIcon className="shrink-0 mt-1 text-primaryDark" />
                  <Typography className="text-sm text-primaryDark">
                    {t(
                      "basket.vehicleRegistration.registrationRegion.explanation"
                    )}
                  </Typography>
                </div>
              </>
            )}
        </div>
      )}

      <div className="flex justify-center pt-4 pb-1 w-full">
        <StickyRequestButton
          data-id="basket-registration-vehicle-continue-button"
          disabled={
            (isSelfRegistrationEnabled &&
              !getIsVehicleRegistrationValid(vehicleRegistration) &&
              !isDefaultRegistrationSelected) ||
            disabled
          }
          fixedHeaderHeight={fixedHeaderHeight}
          isStickyToBottom={true}
          isStickyToTop={true}
          onRequestError={() => {}}
          triggerRequestCallback={onContinueButtonClick}
        >
          {t("general.continue")}
        </StickyRequestButton>
      </div>

      {showChoiceConfirmationModal && (
        <ConfirmChoiceModal
          triggerRequestCallback={handleSelfRegistrationConfirm}
          onRequestSuccess={onContinueButtonClick}
          rejectHandle={() => setIsChoiceConfirmModalOpen(false)}
          isOpen={isChoiceConfirmModalOpen}
        />
      )}
      {showCEESubsidyConfirmationModal && (
        <ConfirmCEESubsidyModal
          onRequestSuccess={onRequestSuccess}
          isOpen={isCEESubsidyConfirmModalOpen}
          ceeSubsidyType={ceeSubsidySelected}
        />
      )}
    </>
  );
};

RegistrationForm.propTypes = {
  deal: dealType.isRequired,
  availableMethods: array.isRequired,
  disabled: bool,
  fixedHeaderHeight: number,
  initialGTMSent: bool,
  onVehicleRegistrationSelected: func.isRequired,
  onVehicleRegistrationConfirmed: func.isRequired,
  updateVehicleRegistration: func,
  openRegistrationCostChangedModal: func,
};

export default RegistrationForm;
